import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { map, Observable, ReplaySubject, switchMap, take, tap, of, Subject, takeUntil } from 'rxjs';
import { SignalRService, SignalRNotification } from 'app/core/services/signalr.service';

@Injectable({ providedIn: 'root' })
export class NotificationsService implements OnDestroy {
    private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<
        Notification[]
    >(1);
    private _destroy$ = new Subject<void>();

    private _staticNotifications: Notification[] = [
        {
            id: 'welcome',
            icon: 'heroicons_outline:sparkles',
            title: 'Welcome to TMA',
            description: 'Your billing analytics platform is ready. Start by selecting a location.',
            time: new Date().toISOString(),
            read: true
        }
    ];

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _signalRService: SignalRService
    ) {
        // Initialize notifications immediately so SignalR updates can be processed
        // even before the notifications dropdown is opened
        this._notifications.next([...this._staticNotifications]);
        this.initSignalRIntegration();
    }

    /**
     * Initialize SignalR integration for background process notifications
     */
    private initSignalRIntegration(): void {
        console.log('[NotificationsService] Initializing SignalR integration...');

        // Subscribe to SignalR progress updates
        this._signalRService.progress$
            .pipe(takeUntil(this._destroy$))
            .subscribe((notification: SignalRNotification) => {
                console.log('[NotificationsService] ===== Received from progress$ =====');
                console.log('[NotificationsService] id:', notification.id, 'progress:', notification.progress);
                this.handleSignalRProgress(notification);
            });

        // Subscribe to SignalR completion notifications
        this._signalRService.notification$
            .pipe(takeUntil(this._destroy$))
            .subscribe((notification: SignalRNotification) => {
                console.log('[NotificationsService] ===== Received from notification$ =====');
                console.log('[NotificationsService] id:', notification.id, 'type:', notification.type);
                this.handleSignalRNotification(notification);
            });

        console.log('[NotificationsService] SignalR subscriptions set up');
    }

    /**
     * Handle SignalR progress updates for background processes
     */
    private handleSignalRProgress(signalRNotification: SignalRNotification): void {
        const isComplete = signalRNotification.status === 'Complete' || signalRNotification.progress === 100;
        const isFailed = signalRNotification.status === 'Failed';

        console.log('[Notifications] handleSignalRProgress:', {
            id: signalRNotification.id,
            progress: signalRNotification.progress,
            status: signalRNotification.status,
            isComplete,
            isFailed,
            message: signalRNotification.message
        });

        // Check if process is complete
        if (isComplete) {
            const success = !signalRNotification.errors || signalRNotification.errors.length === 0;
            const successCount = signalRNotification.metadata?.['successCount'] as number || signalRNotification.currentCount || 0;
            const failureCount = signalRNotification.metadata?.['failureCount'] as number || 0;

            console.log('[Notifications] COMPLETING notification:', signalRNotification.id, 'success:', success);

            this.completeProgressNotification(
                signalRNotification.id,
                signalRNotification.title || 'Import Complete',
                success
                    ? `Successfully processed ${successCount} records` + (failureCount > 0 ? ` (${failureCount} failed)` : '')
                    : `Process completed with errors: ${failureCount} failed`,
                success
            );
        } else if (isFailed) {
            console.log('[Notifications] FAILING notification:', signalRNotification.id);

            this.completeProgressNotification(
                signalRNotification.id,
                signalRNotification.title || 'Import Failed',
                signalRNotification.message || 'Process failed',
                false
            );
        } else {
            // Update progress - create notification object
            const notification: Notification = {
                id: signalRNotification.id,
                icon: 'heroicons_outline:arrow-path',
                title: signalRNotification.title || 'Processing...',
                description: signalRNotification.progressMessage || signalRNotification.message || 'Processing...',
                time: signalRNotification.timestamp?.toISOString() || new Date().toISOString(),
                read: false,
                isProgress: true,
                progress: signalRNotification.progress,
                currentCount: signalRNotification.currentCount,
                totalCount: signalRNotification.totalCount
            };

            console.log('[Notifications] UPDATING progress:', signalRNotification.id, 'progress:', signalRNotification.progress);
            this.upsertProgressNotification(notification);
        }
    }

    /**
     * Handle SignalR completion notifications
     */
    private handleSignalRNotification(signalRNotification: SignalRNotification): void {
        // Only process completion notifications (Success/Error types)
        if (signalRNotification.type === 'Success' || signalRNotification.type === 'Error') {
            const success = signalRNotification.type === 'Success';

            // Check if this is updating an existing progress notification
            this.notifications$.pipe(take(1)).subscribe((notifications) => {
                const existingIndex = notifications.findIndex(n => n.id === signalRNotification.id);
                if (existingIndex !== -1 && notifications[existingIndex].isProgress) {
                    // Complete the progress notification
                    this.completeProgressNotification(
                        signalRNotification.id,
                        signalRNotification.title,
                        signalRNotification.message,
                        success
                    );
                }
            });
        }
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for notifications
     */
    get notifications$(): Observable<Notification[]> {
        return this._notifications.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all notifications
     */
    getAll(): Observable<Notification[]> {
        // Return static notifications for development
        return of([...this._staticNotifications]).pipe(
            tap((notifications) => {
                this._notifications.next(notifications);
            })
        );

        // Original API call commented out for development
        /*
        return this._httpClient
            .get<Notification[]>('api/common/notifications')
            .pipe(
                tap((notifications) => {
                    this._notifications.next(notifications);
                })
            );
        */
    }

    /**
     * Create a notification
     *
     * @param notification
     */
    create(notification: Notification): Observable<Notification> {
        return this.notifications$.pipe(
            take(1),
            switchMap((notifications) => {
                // For development: simulate creating a notification
                const newNotification: Notification = {
                    ...notification,
                    id: Date.now().toString(),
                    time: new Date().toISOString(),
                    read: false
                };

                // Update local notifications
                const updatedNotifications = [...notifications, newNotification];
                this._notifications.next(updatedNotifications);
                this._staticNotifications = updatedNotifications;

                return of(newNotification);

                // Original API call commented out
                /*
                return this._httpClient
                    .post<Notification>('api/common/notifications', {
                        notification,
                    })
                    .pipe(
                        map((newNotification) => {
                            this._notifications.next([
                                ...notifications,
                                newNotification,
                            ]);
                            return newNotification;
                        })
                    );
                */
            })
        );
    }

    /**
     * Update the notification
     *
     * @param id
     * @param notification
     */
    update(id: string, notification: Notification): Observable<Notification> {
        return this.notifications$.pipe(
            take(1),
            switchMap((notifications) => {
                // For development: simulate updating a notification
                const index = notifications.findIndex(item => item.id === id);
                if (index !== -1) {
                    const updatedNotification = { ...notifications[index], ...notification };
                    notifications[index] = updatedNotification;
                    this._notifications.next([...notifications]);
                    this._staticNotifications = [...notifications];
                    return of(updatedNotification);
                }
                return of(notification);

                // Original API call commented out
                /*
                return this._httpClient
                    .patch<Notification>('api/common/notifications', {
                        id,
                        notification,
                    })
                    .pipe(
                        map((updatedNotification: Notification) => {
                            const index = notifications.findIndex(
                                (item) => item.id === id
                            );
                            notifications[index] = updatedNotification;
                            this._notifications.next(notifications);
                            return updatedNotification;
                        })
                    );
                */
            })
        );
    }

    /**
     * Delete the notification
     *
     * @param id
     */
    delete(id: string): Observable<boolean> {
        return this.notifications$.pipe(
            take(1),
            switchMap((notifications) => {
                // For development: simulate deleting a notification
                const index = notifications.findIndex(item => item.id === id);
                if (index !== -1) {
                    notifications.splice(index, 1);
                    this._notifications.next([...notifications]);
                    this._staticNotifications = [...notifications];
                }
                return of(true);

                // Original API call commented out
                /*
                return this._httpClient
                    .delete<boolean>('api/common/notifications', {
                        params: { id },
                    })
                    .pipe(
                        map((isDeleted: boolean) => {
                            const index = notifications.findIndex(
                                (item) => item.id === id
                            );
                            notifications.splice(index, 1);
                            this._notifications.next(notifications);
                            return isDeleted;
                        })
                    );
                */
            })
        );
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): Observable<boolean> {
        return this.notifications$.pipe(
            take(1),
            switchMap((notifications) => {
                // For development: simulate marking all as read
                notifications.forEach((notification, index) => {
                    notifications[index].read = true;
                });
                this._notifications.next([...notifications]);
                this._staticNotifications = [...notifications];
                return of(true);

                // Original API call commented out
                /*
                return this._httpClient
                    .get<boolean>('api/common/notifications/mark-all-as-read')
                    .pipe(
                        map((isUpdated: boolean) => {
                            notifications.forEach((notification, index) => {
                                notifications[index].read = true;
                            });
                            this._notifications.next(notifications);
                            return isUpdated;
                        })
                    );
                */
            })
        );
    }

    /**
     * Create or update a progress notification
     * If a notification with the same ID exists, update it; otherwise create new
     * Uses synchronous update on _staticNotifications to avoid race conditions
     * from rapid SignalR updates
     * IMPORTANT: Will NOT overwrite a completed notification (isProgress=false)
     */
    upsertProgressNotification(notification: Notification): void {
        const index = this._staticNotifications.findIndex(n => n.id === notification.id);

        if (index !== -1) {
            const existing = this._staticNotifications[index];

            // Don't overwrite a completed notification with progress data
            if (existing.isProgress === false) {
                console.log('[Notifications] upsert SKIPPED - notification already completed:', notification.id);
                return;
            }

            // Update existing - merge properties
            console.log('[Notifications] upsert updating:', notification.id, 'progress:', notification.progress);
            this._staticNotifications[index] = {
                ...existing,
                ...notification,
                // Always update progress-related fields
                progress: notification.progress ?? existing.progress,
                currentCount: notification.currentCount ?? existing.currentCount,
                totalCount: notification.totalCount ?? existing.totalCount,
                description: notification.description || existing.description
            };
        } else {
            // Add new at the beginning
            console.log('[Notifications] upsert adding new:', notification.id);
            this._staticNotifications.unshift(notification);
        }
        // Emit new array reference to trigger change detection
        this._notifications.next([...this._staticNotifications]);
    }

    /**
     * Complete a progress notification (convert to regular notification)
     * Uses synchronous update to avoid race conditions
     */
    completeProgressNotification(id: string, title: string, description: string, success: boolean): void {
        console.log('[Notifications] completeProgressNotification called:', { id, title, success });
        console.log('[Notifications] Current notifications:', this._staticNotifications.map(n => ({ id: n.id, isProgress: n.isProgress })));

        const index = this._staticNotifications.findIndex(n => n.id === id);
        console.log('[Notifications] Found notification at index:', index);

        if (index !== -1) {
            this._staticNotifications[index] = {
                ...this._staticNotifications[index],
                isProgress: false,
                progress: undefined,
                currentCount: undefined,
                totalCount: undefined,
                title: title,
                description: description,
                icon: success ? 'heroicons_outline:check-circle' : 'heroicons_outline:x-circle',
                time: new Date().toISOString(),
                read: false
            };
            console.log('[Notifications] Updated notification - isProgress now:', this._staticNotifications[index].isProgress);
            // Emit new array reference to trigger change detection
            this._notifications.next([...this._staticNotifications]);
            console.log('[Notifications] Emitted updated notifications');
        } else {
            console.log('[Notifications] WARNING: Could not find notification with id:', id);
        }
    }
}